// Pitch Form Page - Premium, elegant design for pitch submissions
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Send, Upload, Check, FileText, ChevronDown } from 'lucide-react';
import { getPublicAppConfig, submitAppForm, uploadPitchDeck } from '../../services/api';
import { FIELD_TYPES } from './appConstants';

// Elegant form styles matching the app aesthetic
const styles = {
  container: {
    minHeight: '100vh',
    background: '#FAFAFA',
    padding: '40px 20px',
  },
  card: {
    maxWidth: '580px',
    margin: '0 auto',
    background: '#FFFFFF',
    borderRadius: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    overflow: 'hidden',
  },
  header: {
    padding: '32px 32px 24px',
    borderBottom: '1px solid #F1F5F9',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#64748B',
    textDecoration: 'none',
    marginBottom: '16px',
    transition: 'color 0.2s',
  },
  title: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#1E293B',
    margin: '0 0 4px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748B',
    margin: 0,
  },
  fundSummary: {
    margin: '20px 0 0',
    padding: '16px',
    background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
    borderRadius: '10px',
    border: '1px solid #E2E8F0',
  },
  fundSummaryText: {
    fontSize: '13px',
    color: '#475569',
    lineHeight: '1.6',
    margin: 0,
  },
  fundSummaryPoints: {
    margin: '12px 0 0',
    padding: '0 0 0 16px',
    listStyle: 'none',
  },
  fundSummaryPoint: {
    fontSize: '13px',
    color: '#475569',
    lineHeight: '1.5',
    marginBottom: '6px',
    position: 'relative',
  },
  formBody: {
    padding: '24px 32px 32px',
  },
  fieldGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
  },
  required: {
    color: '#EF4444',
    marginLeft: '2px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    color: '#1E293B',
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  inputFocus: {
    borderColor: '#A78BFA',
    boxShadow: '0 0 0 3px rgba(167, 139, 250, 0.1)',
  },
  textarea: {
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    color: '#1E293B',
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: '8px',
    outline: 'none',
    minHeight: '100px',
    resize: 'vertical',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    color: '#1E293B',
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: '8px',
    outline: 'none',
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: '36px',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  error: {
    fontSize: '12px',
    color: '#EF4444',
    marginTop: '4px',
  },
  submitButton: {
    width: '100%',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#FFFFFF',
    background: '#7C3AED',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'background 0.2s, transform 0.1s',
    marginTop: '8px',
  },
  footer: {
    textAlign: 'center',
    padding: '20px 32px',
    borderTop: '1px solid #F1F5F9',
  },
  footerText: {
    fontSize: '12px',
    color: '#94A3B8',
  },
  successCard: {
    textAlign: 'center',
    padding: '48px 32px',
  },
  successIcon: {
    width: '64px',
    height: '64px',
    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  },
  successTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1E293B',
    margin: '0 0 8px',
  },
  successText: {
    fontSize: '14px',
    color: '#64748B',
    margin: '0 0 24px',
  },
};

export default function PitchFormPage() {
  const { slug, appType } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [appConfig, setAppConfig] = useState(null);
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(null);

  const loadAppConfig = useCallback(async () => {
    try {
      const data = await getPublicAppConfig(slug, appType);
      setAppConfig(data.app);
      setProfile(data.profile);
      
      const initialData = {};
      (data.app.fields || []).forEach(field => {
        initialData[field.id] = '';
      });
      setFormData(initialData);
    } catch (err) {
      console.error('Failed to load app config:', err);
      toast.error('Form not found or not available');
      navigate(`/p/${slug}`);
    } finally {
      setLoading(false);
    }
  }, [slug, appType, navigate]);

  useEffect(() => {
    loadAppConfig();
  }, [loadAppConfig]);

  const handleInputChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) {
      setErrors(prev => ({ ...prev, [fieldId]: null }));
    }
  };

  const handleFileUpload = async (e, fieldId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large (max 25MB)');
      return;
    }

    setUploadingFile(fieldId);
    try {
      const result = await uploadPitchDeck(file);
      setFormData(prev => ({ ...prev, [fieldId]: result.file_url }));
      setFiles(prev => [...prev, { url: result.file_url, name: file.name }]);
      toast.success('File uploaded');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    (appConfig?.fields || []).forEach(field => {
      if (field.required) {
        const value = formData[field.id];
        // For multi-select, check if any option is selected
        if (!value || (typeof value === 'string' && !value.trim())) {
          newErrors[field.id] = 'Required';
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await submitAppForm(slug, appType, formData);
      setSubmitted(true);
      toast.success('Pitch submitted successfully!');
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const isFocused = focusedField === field.id;
    const hasError = errors[field.id];
    
    const inputStyle = {
      ...styles.input,
      ...(isFocused ? styles.inputFocus : {}),
      ...(hasError ? { borderColor: '#EF4444' } : {}),
    };

    switch (field.type) {
      case FIELD_TYPES.TEXTAREA:
        return (
          <textarea
            value={formData[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            onFocus={() => setFocusedField(field.id)}
            onBlur={() => setFocusedField(null)}
            placeholder={field.placeholder}
            style={{
              ...styles.textarea,
              ...(isFocused ? styles.inputFocus : {}),
              ...(hasError ? { borderColor: '#EF4444' } : {}),
            }}
          />
        );
      
      case FIELD_TYPES.DROPDOWN:
        if (field.multiSelect) {
          // Multi-select: render as checkboxes
          const selectedOptions = formData[field.id] ? formData[field.id].split(', ').filter(Boolean) : [];
          return (
            <div style={{
              padding: '12px',
              border: `1px solid ${hasError ? '#EF4444' : '#E2E8F0'}`,
              borderRadius: '8px',
              background: '#FAFAFA',
            }}>
              <p style={{ fontSize: '12px', color: '#64748B', marginBottom: '10px' }}>
                {field.placeholder || 'Select all that apply'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(field.options || []).map((option, i) => {
                  const isChecked = selectedOptions.includes(option);
                  return (
                    <label key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      cursor: 'pointer',
                      padding: '6px 8px',
                      borderRadius: '6px',
                      background: isChecked ? '#F3E8FF' : 'transparent',
                      transition: 'background 0.15s',
                    }}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          let newSelected;
                          if (e.target.checked) {
                            newSelected = [...selectedOptions, option];
                          } else {
                            newSelected = selectedOptions.filter(o => o !== option);
                          }
                          handleInputChange(field.id, newSelected.join(', '));
                        }}
                        style={{
                          width: '16px',
                          height: '16px',
                          accentColor: '#7C3AED',
                          cursor: 'pointer',
                        }}
                      />
                      <span style={{ fontSize: '14px', color: '#374151' }}>{option}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        }
        // Single select: render as dropdown
        return (
          <select
            value={formData[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            onFocus={() => setFocusedField(field.id)}
            onBlur={() => setFocusedField(null)}
            style={{
              ...styles.select,
              ...(isFocused ? styles.inputFocus : {}),
              ...(hasError ? { borderColor: '#EF4444' } : {}),
              color: formData[field.id] ? '#1E293B' : '#9CA3AF',
            }}
          >
            <option value="">{field.placeholder || 'Select...'}</option>
            {(field.options || []).map((option, i) => (
              <option key={i} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case FIELD_TYPES.FILE:
        const isUploading = uploadingFile === field.id;
        const uploadedFile = files.find(f => f.url === formData[field.id]);
        return (
          <div>
            <input
              type="file"
              onChange={(e) => handleFileUpload(e, field.id)}
              id={`file-${field.id}`}
              accept=".pdf,.doc,.docx,.ppt,.pptx"
              disabled={isUploading}
              style={{ display: 'none' }}
            />
            <label
              htmlFor={`file-${field.id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                width: '100%',
                padding: '14px',
                border: `1.5px dashed ${formData[field.id] ? '#10B981' : '#E2E8F0'}`,
                borderRadius: '8px',
                cursor: isUploading ? 'wait' : 'pointer',
                background: formData[field.id] ? '#F0FDF4' : '#FAFAFA',
                transition: 'all 0.2s',
              }}
            >
              {isUploading ? (
                <>
                  <Loader2 size={18} className="animate-spin" style={{ color: '#7C3AED' }} />
                  <span style={{ fontSize: '13px', color: '#7C3AED' }}>Uploading...</span>
                </>
              ) : formData[field.id] ? (
                <>
                  <FileText size={18} style={{ color: '#10B981' }} />
                  <span style={{ fontSize: '13px', color: '#10B981', fontWeight: '500' }}>
                    {uploadedFile?.name || 'File uploaded'}
                  </span>
                  <Check size={16} style={{ color: '#10B981' }} />
                </>
              ) : (
                <>
                  <Upload size={18} style={{ color: '#64748B' }} />
                  <span style={{ fontSize: '13px', color: '#64748B' }}>
                    {field.placeholder || 'Upload file'} 
                    <span style={{ opacity: 0.6 }}> (PDF, DOC, PPT)</span>
                  </span>
                </>
              )}
            </label>
          </div>
        );
      
      default:
        return (
          <input
            type="text"
            value={formData[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            onFocus={() => setFocusedField(field.id)}
            onBlur={() => setFocusedField(null)}
            placeholder={field.placeholder}
            style={inputStyle}
          />
        );
    }
  };

  if (loading) {
    return (
      <div style={{ ...styles.container, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 size={32} className="animate-spin" style={{ color: '#7C3AED' }} />
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successCard}>
            <div style={styles.successIcon}>
              <Check size={32} style={{ color: '#FFFFFF' }} />
            </div>
            <h2 style={styles.successTitle}>Pitch Received!</h2>
            <p style={styles.successText}>
              Thank you for your submission. {profile?.name?.split(' ')[0] || 'They'} will review it shortly.
            </p>
            <Link
              to={`/p/${slug}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                color: '#7C3AED',
                textDecoration: 'none',
                fontWeight: '500',
              }}
            >
              <ArrowLeft size={16} />
              Back to profile
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <Link to={`/p/${slug}`} style={styles.backLink}>
            <ArrowLeft size={14} />
            Back to {profile?.name?.split(' ')[0] || 'profile'}
          </Link>
          <h1 style={styles.title}>{appConfig?.title || 'Submit Your Pitch'}</h1>
          <p style={styles.subtitle}>
            Pitching to {profile?.name || 'this founder'}
          </p>
          
          {/* Fund Summary */}
          {appConfig?.fund_summary && (
            <div style={styles.fundSummary}>
              <p style={styles.fundSummaryText}>{appConfig.fund_summary}</p>
              {appConfig?.fund_points?.length > 0 && (
                <ul style={styles.fundSummaryPoints}>
                  {appConfig.fund_points.map((point, i) => (
                    <li key={i} style={styles.fundSummaryPoint}>
                      <span style={{ position: 'absolute', left: '-14px', color: '#7C3AED' }}>•</span>
                      {point}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.formBody}>
          {(appConfig?.fields || []).map((field) => (
            <div key={field.id} style={styles.fieldGroup}>
              <label style={styles.label}>
                {field.label}
                {field.required && <span style={styles.required}>*</span>}
              </label>
              {renderField(field)}
              {errors[field.id] && (
                <div style={styles.error}>{errors[field.id]}</div>
              )}
            </div>
          ))}

          <button
            type="submit"
            disabled={submitting}
            style={{
              ...styles.submitButton,
              opacity: submitting ? 0.7 : 1,
              cursor: submitting ? 'wait' : 'pointer',
            }}
            onMouseOver={(e) => { if (!submitting) e.currentTarget.style.background = '#6D28D9'; }}
            onMouseOut={(e) => { if (!submitting) e.currentTarget.style.background = '#7C3AED'; }}
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send size={16} />
                Submit Pitch
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div style={styles.footer}>
          <p style={styles.footerText}>
            Powered by <a href="https://brief.link" target="_blank" rel="noopener noreferrer" style={{ color: '#7C3AED', textDecoration: 'none' }}>Brief</a>
          </p>
        </div>
      </div>
    </div>
  );
}
